import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Plus, Pencil, Trash2, KeyRound, Search, ShieldCheck, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/app/AppShell";
import { StatCard, Spinner, StatusBadge, EmptyState } from "@/components/common/Common";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const errMsg = (e) => formatApiErrorDetail(e?.response?.data?.detail);
const inr = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs text-slate-500">{label}</Label>{children}</div>;
}

export default function AdminPanel() {
  const { user } = useAuth();
  const [ov, setOv] = useState(null);
  const loadOv = useCallback(() => api.get("/admin/overview").then(({ data }) => setOv(data)).catch(() => {}), []);
  useEffect(() => { loadOv(); }, [loadOv]);

  if (!ov) return <AppShell variant="business" title="Admin"><Spinner /></AppShell>;

  return (
    <AppShell variant="business" title="Platform Admin">
      <div className="flex items-center gap-2 mb-6" data-testid="admin-panel">
        <Building2 className="h-5 w-5 text-sp-orange" />
        <h1 className="text-2xl font-bold text-sp-navy">Platform Admin</h1>
        <StatusBadge status={ov.system_health} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Users" value={ov.users} accent testid="stat-users" />
        <StatCard label="Verified" value={ov.verified_users} sub={`${ov.homeowners} homeowners · ${ov.businesses} business`} />
        <StatCard label="Organizations" value={ov.organizations} testid="stat-orgs" />
        <StatCard label="Projects" value={ov.projects} sub={`${ov.active_projects} active`} testid="stat-projects" />
        <StatCard label="Documents" value={ov.documents} />
        <StatCard label="AI Calls" value={ov.ai_calls} sub={`${ov.ai_conversations} conversations`} />
        <StatCard label="Marketplace" value={ov.marketplace_products} />
        <StatCard label="Plans" value={ov.plans} />
      </div>

      <Tabs defaultValue="users" className="mt-6">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="projects" data-testid="admin-tab-projects">Projects</TabsTrigger>
          <TabsTrigger value="orgs" data-testid="admin-tab-orgs">Organizations</TabsTrigger>
          <TabsTrigger value="marketplace" data-testid="admin-tab-marketplace">Marketplace</TabsTrigger>
          <TabsTrigger value="plans" data-testid="admin-tab-plans">Plans</TabsTrigger>
          <TabsTrigger value="blog" data-testid="admin-tab-blog">Blog</TabsTrigger>
        </TabsList>
        <TabsContent value="users"><UsersTab currentUserId={user?.id} onChange={loadOv} /></TabsContent>
        <TabsContent value="projects"><ProjectsTab onChange={loadOv} /></TabsContent>
        <TabsContent value="orgs"><OrgsTab onChange={loadOv} /></TabsContent>
        <TabsContent value="marketplace"><MarketplaceTab onChange={loadOv} /></TabsContent>
        <TabsContent value="plans"><PlansTab onChange={loadOv} /></TabsContent>
        <TabsContent value="blog"><BlogTab /></TabsContent>
      </Tabs>
    </AppShell>
  );
}

function Toolbar({ q, setQ, placeholder, onAdd, addLabel, testid }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      {setQ && (
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid={`${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder} className="pl-9 h-9 bg-white" />
        </div>
      )}
      <div className="flex-1" />
      {onAdd && <Button data-testid={`${testid}-add`} onClick={onAdd} className="bg-sp-orange hover:bg-sp-orangedark rounded-full"><Plus className="h-4 w-4 mr-1" />{addLabel}</Button>}
    </div>
  );
}

function TableWrap({ children }) {
  return <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">{children}</div>;
}

function DeleteConfirm({ open, onOpenChange, onConfirm, title, desc, testid }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testid}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{desc}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="delete-confirm" onClick={onConfirm} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ---------------- Users ---------------- */
function UsersTab({ currentUserId, onChange }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);       // user obj or {} for create
  const [pwUser, setPwUser] = useState(null);
  const [delUser, setDelUser] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/users", { params: q ? { q } : {} }).then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const refresh = () => { load(); onChange?.(); };

  return (
    <>
      <Toolbar q={q} setQ={setQ} placeholder="Search name or email" testid="users"
        onAdd={() => setEdit({ _new: true, role: "user", account_type: "homeowner", password: "" })} addLabel="Add user" />
      {!rows ? <Spinner /> : rows.length === 0 ? <EmptyState title="No users" /> : (
        <TableWrap>
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Type</TableHead><TableHead>Verified</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-slate-500">{u.email}</TableCell>
                  <TableCell><StatusBadge status={u.role} /></TableCell>
                  <TableCell>{u.account_type || "—"}</TableCell>
                  <TableCell>{u.email_verified ? <StatusBadge status="Verified" /> : <StatusBadge status="Pending" />}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" data-testid={`user-edit-${u.id}`} onClick={() => setEdit({ ...u })}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`user-pw-${u.id}`} onClick={() => setPwUser(u)}><KeyRound className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" disabled={u.id === currentUserId} data-testid={`user-del-${u.id}`} onClick={() => setDelUser(u)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrap>
      )}

      {edit && <UserDialog user={edit} onClose={() => setEdit(null)} onSaved={refresh} />}
      {pwUser && <PasswordDialog user={pwUser} onClose={() => setPwUser(null)} />}
      <DeleteConfirm open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)} testid="user-delete-dialog"
        title="Delete user?" desc={`This permanently removes ${delUser?.name} (${delUser?.email}).`}
        onConfirm={async () => {
          try { await api.delete(`/admin/users/${delUser.id}`); toast.success("User deleted"); setDelUser(null); refresh(); }
          catch (e) { toast.error(errMsg(e)); }
        }} />
    </>
  );
}

function UserDialog({ user, onClose, onSaved }) {
  const isNew = !!user._new;
  const [f, setF] = useState({
    name: user.name || "", email: user.email || "", password: "",
    role: user.role || "user", account_type: user.account_type || "homeowner",
    email_verified: user.email_verified ?? false, onboarded: user.onboarded ?? false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        await api.post("/admin/users", { name: f.name, email: f.email, password: f.password, role: f.role, account_type: f.account_type });
        toast.success("User created");
      } else {
        await api.patch(`/admin/users/${user.id}`, { name: f.name, role: f.role, account_type: f.account_type, email_verified: f.email_verified, onboarded: f.onboarded });
        toast.success("User updated");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add user" : "Edit user"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Full name"><Input data-testid="user-name" value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
          {isNew && <Field label="Email"><Input data-testid="user-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>}
          {isNew && <Field label="Password"><Input data-testid="user-password" type="text" value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="Min 6 characters" /></Field>}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Role">
              <Select value={f.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="user">User</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Account type">
              <Select value={f.account_type || "none"} onValueChange={(v) => set("account_type", v === "none" ? null : v)}>
                <SelectTrigger data-testid="user-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="homeowner">Homeowner</SelectItem><SelectItem value="business">Business</SelectItem><SelectItem value="none">Unset</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          {!isNew && (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
              <div className="flex items-center gap-2 text-sm"><ShieldCheck className="h-4 w-4 text-emerald-500" />Email verified</div>
              <Switch data-testid="user-verified" checked={f.email_verified} onCheckedChange={(v) => set("email_verified", v)} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="user-save" onClick={save} disabled={busy} className="bg-sp-orange hover:bg-sp-orangedark">{busy ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PasswordDialog({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/admin/users/${user.id}/reset-password`, { password: pw }); toast.success("Password reset"); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="password-dialog">
        <DialogHeader><DialogTitle>Reset password — {user.name}</DialogTitle></DialogHeader>
        <Field label="New password"><Input data-testid="new-password" type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Min 6 characters" /></Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="password-save" onClick={save} disabled={busy || pw.length < 6} className="bg-sp-orange hover:bg-sp-orangedark">{busy ? "Saving..." : "Reset"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Projects ---------------- */
const CLIENT_STAGES = ["Land only", "Planning", "Design", "Approvals", "Contractor selected", "Construction started"];

function ProjectsTab({ onChange }) {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [creating, setCreating] = useState(false);
  const [orgs, setOrgs] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/projects", { params: q ? { q } : {} }).then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/admin/organizations").then(({ data }) => setOrgs(data)).catch(() => {}); }, []);
  const refresh = () => { load(); onChange?.(); };

  return (
    <>
      <Toolbar q={q} setQ={setQ} placeholder="Search project or location" testid="projects"
        onAdd={() => setCreating(true)} addLabel="New project for client" />
      {!rows ? <Spinner /> : rows.length === 0 ? <EmptyState title="No projects" actionLabel="New project for client" onAction={() => setCreating(true)} /> : (
        <TableWrap>
          <Table>
            <TableHeader><TableRow><TableHead>Project</TableHead><TableHead>Client / Location</TableHead><TableHead>Budget</TableHead><TableHead>Progress</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} data-testid={`project-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-slate-500">{p.owner_name || "—"}{p.location ? ` · ${p.location}` : ""}</TableCell>
                  <TableCell>{inr(p.budget)}</TableCell>
                  <TableCell><div className="flex items-center gap-2"><div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden sm:block"><div className="h-full bg-sp-orange" style={{ width: `${p.progress ?? 0}%` }} /></div><span>{p.progress ?? 0}%</span></div></TableCell>
                  <TableCell><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" data-testid={`project-manage-${p.id}`} onClick={() => nav(`/app/project/${p.id}`)} className="text-sp-orange"><Settings2 className="h-4 w-4 mr-1" />Manage</Button>
                    <Button size="icon" variant="ghost" data-testid={`project-edit-${p.id}`} onClick={() => setEdit({ ...p })}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`project-del-${p.id}`} onClick={() => setDel(p)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrap>
      )}
      {creating && <NewProjectDialog orgs={orgs} onClose={() => setCreating(false)}
        onCreated={(pid) => { setCreating(false); refresh(); nav(`/app/project/${pid}`); }} />}
      {edit && <ProjectDialog project={edit} onClose={() => setEdit(null)} onSaved={refresh} />}
      <DeleteConfirm open={!!del} onOpenChange={(o) => !o && setDel(null)} testid="project-delete-dialog"
        title="Delete project?" desc={`This permanently removes "${del?.name}" and all its data (BOQ, tasks, budget, documents).`}
        onConfirm={async () => {
          try { await api.delete(`/admin/projects/${del.id}`); toast.success("Project deleted"); setDel(null); refresh(); }
          catch (e) { toast.error(errMsg(e)); }
        }} />
    </>
  );
}

function NewProjectDialog({ orgs, onClose, onCreated }) {
  const [f, setF] = useState({
    name: "", client_email: "", client_name: "", organization_id: "none",
    construction_type: "Independent House", location: "", builtup_area: "", floors: "G+1",
    budget: "", current_stage: "Planning", manager_email: "", engineer_email: "", send_invite: true,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name) return toast.error("Project name required");
    if (!f.client_email) return toast.error("Client email required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/projects/create-for-client", {
        name: f.name, client_email: f.client_email, client_name: f.client_name || null,
        organization_id: f.organization_id === "none" ? null : f.organization_id,
        construction_type: f.construction_type, location: f.location,
        builtup_area: Number(f.builtup_area) || 0, floors: f.floors,
        budget: Number(f.budget) || 0, current_stage: f.current_stage,
        manager_email: f.manager_email || null, engineer_email: f.engineer_email || null,
        send_invite: f.send_invite,
      });
      toast.success(`Project created${data.invited ? " · client emailed" : ""} — opening console`, {
        description: data.temp_password ? `New client login → ${f.client_email} / ${data.temp_password}` : undefined,
        duration: data.temp_password ? 15000 : 4000,
      });
      onCreated(data.project.id);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-project-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create project for a client</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Project name"><Input data-testid="np-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Sharma Residence" /></Field>
          <Field label="Assign to organization">
            <Select value={f.organization_id} onValueChange={(v) => set("organization_id", v)}>
              <SelectTrigger data-testid="np-org"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">None (homeowner project)</SelectItem>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Client email"><Input data-testid="np-client-email" type="email" value={f.client_email} onChange={(e) => set("client_email", e.target.value)} placeholder="client@email.com" /></Field>
          <Field label="Client name (if new)"><Input data-testid="np-client-name" value={f.client_name} onChange={(e) => set("client_name", e.target.value)} placeholder="Auto-created if not registered" /></Field>
          <Field label="Type">
            <Select value={f.construction_type} onValueChange={(v) => set("construction_type", v)}>
              <SelectTrigger data-testid="np-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["Independent House", "Villa", "Apartment", "Commercial", "Interior", "Renovation"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Starting stage">
            <Select value={f.current_stage} onValueChange={(v) => set("current_stage", v)}>
              <SelectTrigger data-testid="np-stage"><SelectValue /></SelectTrigger>
              <SelectContent>{CLIENT_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Location"><Input value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="Bengaluru" /></Field>
          <Field label="Built-up area (sqft)"><Input type="number" value={f.builtup_area} onChange={(e) => set("builtup_area", e.target.value)} /></Field>
          <Field label="Floors"><Input value={f.floors} onChange={(e) => set("floors", e.target.value)} placeholder="G+1" /></Field>
          <Field label="Budget (₹)"><Input type="number" value={f.budget} onChange={(e) => set("budget", e.target.value)} /></Field>
          <Field label="Project manager email"><Input data-testid="np-manager" value={f.manager_email} onChange={(e) => set("manager_email", e.target.value)} placeholder="Optional — existing user" /></Field>
          <Field label="Site engineer email"><Input data-testid="np-engineer" value={f.engineer_email} onChange={(e) => set("engineer_email", e.target.value)} placeholder="Optional — existing user" /></Field>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2.5">
          <span className="text-sm text-sp-navy">Email the client their login &amp; portal link</span>
          <Switch data-testid="np-invite" checked={f.send_invite} onCheckedChange={(v) => set("send_invite", v)} />
        </div>
        <p className="text-xs text-slate-400">A full project is initialized (stages Planning→Handover, tasks, folders, budget, starter BOQ &amp; AI context). You'll be taken straight into the console to manage every stage.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="np-save" onClick={save} disabled={busy} className="bg-sp-orange hover:bg-sp-orangedark">{busy ? "Creating…" : "Create & Open Console"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProjectDialog({ project, onClose, onSaved }) {
  const [f, setF] = useState({
    name: project.name || "", location: project.location || "",
    status: project.status || "Active", progress: project.progress ?? 0, budget: project.budget ?? 0,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/projects/${project.id}`, { name: f.name, location: f.location, status: f.status, progress: Number(f.progress), budget: Number(f.budget) });
      toast.success("Project updated"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="project-dialog">
        <DialogHeader><DialogTitle>Edit project</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Name"><Input data-testid="project-name" value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Location"><Input data-testid="project-location" value={f.location} onChange={(e) => set("location", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Status">
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="project-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="On Hold">On Hold</SelectItem><SelectItem value="Completed">Completed</SelectItem><SelectItem value="Cancelled">Cancelled</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Progress %"><Input data-testid="project-progress" type="number" min="0" max="100" value={f.progress} onChange={(e) => set("progress", e.target.value)} /></Field>
          </div>
          <Field label="Budget (₹)"><Input data-testid="project-budget" type="number" value={f.budget} onChange={(e) => set("budget", e.target.value)} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="project-save" onClick={save} disabled={busy} className="bg-sp-orange hover:bg-sp-orangedark">{busy ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Organizations ---------------- */
function OrgsTab({ onChange }) {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get("/admin/organizations").then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChange?.(); };
  return (
    <>
      <Toolbar testid="orgs" />
      {!rows ? <Spinner /> : rows.length === 0 ? <EmptyState title="No organizations" /> : (
        <TableWrap>
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>City</TableHead><TableHead>Members</TableHead><TableHead>Projects</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((o) => (
                <TableRow key={o.id} data-testid={`org-row-${o.id}`}>
                  <TableCell className="font-medium">{o.name}</TableCell>
                  <TableCell className="text-slate-500">{o.business_type || "—"}</TableCell>
                  <TableCell>{o.city || "—"}</TableCell>
                  <TableCell>{o.member_count}</TableCell>
                  <TableCell>{o.project_count}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" data-testid={`org-edit-${o.id}`} onClick={() => setEdit({ ...o })}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`org-del-${o.id}`} onClick={() => setDel(o)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrap>
      )}
      {edit && <OrgDialog org={edit} onClose={() => setEdit(null)} onSaved={refresh} />}
      <DeleteConfirm open={!!del} onOpenChange={(o) => !o && setDel(null)} testid="org-delete-dialog"
        title="Delete organization?" desc={`This removes "${del?.name}" and its memberships.`}
        onConfirm={async () => {
          try { await api.delete(`/admin/organizations/${del.id}`); toast.success("Organization deleted"); setDel(null); refresh(); }
          catch (e) { toast.error(errMsg(e)); }
        }} />
    </>
  );
}

function OrgDialog({ org, onClose, onSaved }) {
  const [f, setF] = useState({
    name: org.name || "", business_type: org.business_type || "", city: org.city || "",
    phone: org.phone || "", gstin: org.gstin || "", pan: org.pan || "", address: org.address || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/admin/organizations/${org.id}`, f); toast.success("Organization updated"); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="org-dialog">
        <DialogHeader><DialogTitle>Edit organization</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Name"><Input data-testid="org-name" value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Business type"><Input data-testid="org-type" value={f.business_type} onChange={(e) => set("business_type", e.target.value)} /></Field>
            <Field label="City"><Input data-testid="org-city" value={f.city} onChange={(e) => set("city", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Phone"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="GSTIN"><Input value={f.gstin} onChange={(e) => set("gstin", e.target.value)} /></Field>
          </div>
          <Field label="Address"><Input value={f.address} onChange={(e) => set("address", e.target.value)} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="org-save" onClick={save} disabled={busy} className="bg-sp-orange hover:bg-sp-orangedark">{busy ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Marketplace ---------------- */
function MarketplaceTab({ onChange }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get("/admin/marketplace", { params: q ? { q } : {} }).then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e))), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const refresh = () => { load(); onChange?.(); };
  return (
    <>
      <Toolbar q={q} setQ={setQ} placeholder="Search products" testid="marketplace"
        onAdd={() => setEdit({ _new: true, section: "Materials", availability: "In Stock", rating: 4.6 })} addLabel="Add product" />
      {!rows ? <Spinner /> : rows.length === 0 ? <EmptyState title="No products" /> : (
        <TableWrap>
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Section</TableHead><TableHead>Category</TableHead><TableHead>Price</TableHead><TableHead>Seller</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell><StatusBadge status={p.section} /></TableCell>
                  <TableCell className="text-slate-500">{p.category}</TableCell>
                  <TableCell>{p.price != null ? `${inr(p.price)}/${p.unit || ""}` : "—"}</TableCell>
                  <TableCell className="text-slate-500">{p.seller || "—"}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" data-testid={`product-edit-${p.id}`} onClick={() => setEdit({ ...p })}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`product-del-${p.id}`} onClick={() => setDel(p)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrap>
      )}
      {edit && <ProductDialog product={edit} onClose={() => setEdit(null)} onSaved={refresh} />}
      <DeleteConfirm open={!!del} onOpenChange={(o) => !o && setDel(null)} testid="product-delete-dialog"
        title="Delete product?" desc={`This removes "${del?.name}" from the marketplace.`}
        onConfirm={async () => {
          try { await api.delete(`/admin/marketplace/${del.id}`); toast.success("Product deleted"); setDel(null); refresh(); }
          catch (e) { toast.error(errMsg(e)); }
        }} />
    </>
  );
}

function ProductDialog({ product, onClose, onSaved }) {
  const isNew = !!product._new;
  const [f, setF] = useState({
    name: product.name || "", section: product.section || "Materials", category: product.category || "",
    price: product.price ?? "", unit: product.unit || "", location: product.location || "",
    seller: product.seller || "", availability: product.availability || "In Stock", rating: product.rating ?? 4.6,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    const payload = { ...f, price: f.price === "" ? null : Number(f.price), rating: Number(f.rating) };
    try {
      if (isNew) { await api.post("/admin/marketplace", payload); toast.success("Product created"); }
      else { await api.patch(`/admin/marketplace/${product.id}`, payload); toast.success("Product updated"); }
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="product-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add product" : "Edit product"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Name"><Input data-testid="product-name" value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Section">
              <Select value={f.section} onValueChange={(v) => set("section", v)}>
                <SelectTrigger data-testid="product-section"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Materials">Materials</SelectItem><SelectItem value="Services">Services</SelectItem><SelectItem value="Equipment">Equipment</SelectItem><SelectItem value="Professionals">Professionals</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Category"><Input data-testid="product-category" value={f.category} onChange={(e) => set("category", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Price (₹)"><Input data-testid="product-price" type="number" value={f.price} onChange={(e) => set("price", e.target.value)} /></Field>
            <Field label="Unit"><Input data-testid="product-unit" value={f.unit} onChange={(e) => set("unit", e.target.value)} placeholder="bag, sqft, tonne..." /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Location"><Input value={f.location} onChange={(e) => set("location", e.target.value)} /></Field>
            <Field label="Seller"><Input value={f.seller} onChange={(e) => set("seller", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Availability">
              <Select value={f.availability} onValueChange={(v) => set("availability", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="In Stock">In Stock</SelectItem><SelectItem value="Limited">Limited</SelectItem><SelectItem value="Out of Stock">Out of Stock</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Rating"><Input type="number" step="0.1" min="0" max="5" value={f.rating} onChange={(e) => set("rating", e.target.value)} /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="product-save" onClick={save} disabled={busy} className="bg-sp-orange hover:bg-sp-orangedark">{busy ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Plans ---------------- */
function PlansTab({ onChange }) {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get("/admin/plans").then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChange?.(); };
  return (
    <>
      <Toolbar testid="plans" onAdd={() => setEdit({ _new: true, segment: "B2C", features: [] })} addLabel="Add plan" />
      {!rows ? <Spinner /> : rows.length === 0 ? <EmptyState title="No plans" /> : (
        <TableWrap>
          <Table>
            <TableHeader><TableRow><TableHead>Plan</TableHead><TableHead>Segment</TableHead><TableHead>Monthly</TableHead><TableHead>Users</TableHead><TableHead>Projects</TableHead><TableHead>AI calls</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} data-testid={`plan-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell><StatusBadge status={p.segment} /></TableCell>
                  <TableCell>{inr(p.monthly)}</TableCell>
                  <TableCell>{p.users}</TableCell>
                  <TableCell>{p.projects}</TableCell>
                  <TableCell>{p.ai_calls}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" data-testid={`plan-edit-${p.id}`} onClick={() => setEdit({ ...p })}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`plan-del-${p.id}`} onClick={() => setDel(p)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrap>
      )}
      {edit && <PlanDialog plan={edit} onClose={() => setEdit(null)} onSaved={refresh} />}
      <DeleteConfirm open={!!del} onOpenChange={(o) => !o && setDel(null)} testid="plan-delete-dialog"
        title="Delete plan?" desc={`This removes the "${del?.name}" subscription plan.`}
        onConfirm={async () => {
          try { await api.delete(`/admin/plans/${del.id}`); toast.success("Plan deleted"); setDel(null); refresh(); }
          catch (e) { toast.error(errMsg(e)); }
        }} />
    </>
  );
}

function PlanDialog({ plan, onClose, onSaved }) {
  const isNew = !!plan._new;
  const [f, setF] = useState({
    id: plan.id || "", segment: plan.segment || "B2C", name: plan.name || "",
    monthly: plan.monthly ?? 0, annual: plan.annual ?? 0, users: plan.users ?? 1,
    projects: plan.projects ?? 1, storage_gb: plan.storage_gb ?? 1, ai_calls: plan.ai_calls ?? 0,
    features: (plan.features || []).join("\n"),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    const payload = {
      segment: f.segment, name: f.name, monthly: Number(f.monthly), annual: Number(f.annual),
      users: Number(f.users), projects: Number(f.projects), storage_gb: Number(f.storage_gb),
      ai_calls: Number(f.ai_calls), features: f.features.split("\n").map((x) => x.trim()).filter(Boolean),
    };
    try {
      if (isNew) { await api.post("/admin/plans", { id: f.id, ...payload }); toast.success("Plan created"); }
      else { await api.patch(`/admin/plans/${plan.id}`, payload); toast.success("Plan updated"); }
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="plan-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isNew ? "Add plan" : "Edit plan"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {isNew && <Field label="Plan ID"><Input data-testid="plan-id" value={f.id} onChange={(e) => set("id", e.target.value)} placeholder="e.g. home_pro" /></Field>}
            <Field label="Name"><Input data-testid="plan-name" value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label="Segment">
              <Select value={f.segment} onValueChange={(v) => set("segment", v)}>
                <SelectTrigger data-testid="plan-segment"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="B2C">B2C</SelectItem><SelectItem value="B2B">B2B</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Monthly (₹)"><Input data-testid="plan-monthly" type="number" value={f.monthly} onChange={(e) => set("monthly", e.target.value)} /></Field>
            <Field label="Annual (₹)"><Input type="number" value={f.annual} onChange={(e) => set("annual", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Users"><Input type="number" value={f.users} onChange={(e) => set("users", e.target.value)} /></Field>
            <Field label="Projects"><Input type="number" value={f.projects} onChange={(e) => set("projects", e.target.value)} /></Field>
            <Field label="Storage GB"><Input type="number" value={f.storage_gb} onChange={(e) => set("storage_gb", e.target.value)} /></Field>
          </div>
          <Field label="AI calls"><Input type="number" value={f.ai_calls} onChange={(e) => set("ai_calls", e.target.value)} /></Field>
          <Field label="Features (one per line)"><Textarea data-testid="plan-features" rows={4} value={f.features} onChange={(e) => set("features", e.target.value)} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="plan-save" onClick={save} disabled={busy} className="bg-sp-orange hover:bg-sp-orangedark">{busy ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ---------------- Blog ---------------- */
const CATEGORIES = ["Guides", "Industry", "Product", "Technology", "Finance", "General"];

function BlogTab() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/blog/posts").then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const remove = async (p) => { if (!window.confirm(`Delete "${p.title}"?`)) return; await api.delete(`/admin/blog/posts/${p.id}`); toast.success("Deleted"); load(); };
  const openEdit = async (p) => { const { data } = await api.get(`/admin/blog/posts/${p.id}`); setEdit(data); };
  return (
    <>
      <Toolbar onAdd={() => setEdit({ title: "", excerpt: "", content: "", category: "Guides", tags: [], author: "Sitepar Team", status: "draft" })} addLabel="New post" testid="blog" />
      {!rows ? <Spinner /> : rows.length === 0 ? <EmptyState title="No posts yet" actionLabel="New post" onAction={() => setEdit({ title: "", excerpt: "", content: "", category: "Guides", tags: [], author: "Sitepar Team", status: "draft" })} /> : (
        <TableWrap><Table>
          <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Status</TableHead><TableHead>Read</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((p) => (
            <TableRow key={p.id} data-testid={`blog-row-${p.id}`}>
              <TableCell className="font-medium">{p.title}</TableCell>
              <TableCell className="text-slate-500">{p.category}</TableCell>
              <TableCell><StatusBadge status={p.status} /></TableCell>
              <TableCell>{p.read_time}m</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <Button size="icon" variant="ghost" data-testid={`blog-edit-${p.id}`} onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" data-testid={`blog-del-${p.id}`} onClick={() => remove(p)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </TableCell>
            </TableRow>))}</TableBody>
        </Table></TableWrap>
      )}
      {edit && <BlogDialog post={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </>
  );
}

function BlogDialog({ post, onClose, onSaved }) {
  const isNew = !post.id;
  const [f, setF] = useState({ ...post, tags: (post.tags || []).join(", ") });
  const [busy, setBusy] = useState(false);
  const [topic, setTopic] = useState("");
  const [drafting, setDrafting] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const aiDraft = async () => {
    if (!topic.trim()) return toast.error("Enter a topic for the AI");
    setDrafting(true);
    try {
      const { data } = await api.post("/admin/blog/ai-draft", { topic });
      setF((p) => ({ ...p, title: data.title || p.title, excerpt: data.excerpt || p.excerpt, content: data.content || p.content, category: data.category || p.category, tags: (data.tags || []).join(", ") }));
      toast.success("AI draft ready — review & publish");
    } catch (e) { toast.error(errMsg(e)); } finally { setDrafting(false); }
  };
  const save = async (status) => {
    setBusy(true);
    const payload = { title: f.title, excerpt: f.excerpt, content: f.content, cover_image: f.cover_image || null, category: f.category, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean), author: f.author || "Sitepar Team", status: status || f.status };
    try {
      if (isNew) await api.post("/admin/blog/posts", payload); else await api.patch(`/admin/blog/posts/${post.id}`, payload);
      toast.success(payload.status === "published" ? "Published" : "Saved"); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="blog-dialog" className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isNew ? "New post" : "Edit post"}</DialogTitle></DialogHeader>
        <div className="rounded-lg border border-sp-orange/30 bg-sp-orange/5 p-3 mb-2">
          <Label className="text-xs text-sp-orange font-semibold">✨ AI draft assistant</Label>
          <div className="flex gap-2 mt-1.5">
            <Input data-testid="ai-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Choosing the right cement grade" />
            <Button data-testid="ai-draft-btn" onClick={aiDraft} disabled={drafting} className="bg-sp-navy hover:bg-sp-navy/90 shrink-0">{drafting ? "Writing…" : "Draft with AI"}</Button>
          </div>
        </div>
        <div className="space-y-3">
          <Field label="Title"><Input data-testid="blog-title" value={f.title} onChange={(e) => set("title", e.target.value)} /></Field>
          <Field label="Excerpt"><Textarea rows={2} data-testid="blog-excerpt" value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Category"><Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="blog-category"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Author"><Input value={f.author} onChange={(e) => set("author", e.target.value)} /></Field>
            <Field label="Cover image URL"><Input value={f.cover_image || ""} onChange={(e) => set("cover_image", e.target.value)} /></Field>
          </div>
          <Field label="Tags (comma separated)"><Input data-testid="blog-tags" value={f.tags} onChange={(e) => set("tags", e.target.value)} /></Field>
          <Field label="Content (HTML)"><Textarea rows={10} data-testid="blog-content" value={f.content} onChange={(e) => set("content", e.target.value)} className="font-mono text-xs" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => save("draft")} disabled={busy}>Save draft</Button>
          <Button data-testid="blog-publish" onClick={() => save("published")} disabled={busy} className="bg-sp-orange hover:bg-sp-orangedark">{busy ? "Saving…" : "Publish"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
